import React from 'react';
import { Button } from './ui/button';

// Sketched-placeholder collage for a collection with 0 recipes.
// Used in two places:
//   - Owner's own page: heading "Save your first recipe" + live Add Recipe
//     button (pass onAddRecipe).
//   - Someone else's empty public page: heading "No recipes added yet",
//     no button (omit onAddRecipe).
//
// Each artwork file has its own margins baked in (the drawn cards start
// slightly inside the image edges), so every variant oversizes and shifts
// the image to line the drawn cards up with the page container edges —
// matching the profile section's margins. The outer box clips the artwork's
// own margin area, but extends 8px beyond the container on each side so the
// sketched card outlines that stray past the card edge aren't cropped.
// The heading card is positioned with percentages inside the same
// aspect-ratio box as the artwork, so it scales and moves WITH the collage
// on resize — never floating off as a separate thing.

const keepFinalWordsTogether = (copy) =>
  copy.replace(/\s+(\S+\s+\S+)$/, '\u00A0$1');

const HeadingCard = ({ heading, onAddRecipe, headingSize, fullWidthButton }) => (
  <div
    className="w-full h-full rounded-lg flex flex-col items-start justify-center p-4 md:p-6"
    style={{ backgroundColor: '#F3EEE4', border: '1px dashed #D6D0C2' }}
    data-testid="first-recipe-cta"
  >
    <h2
      style={{
        fontFamily: "'Instrument Serif', serif",
        color: '#51120A',
        fontSize: headingSize,
        lineHeight: 1.05,
        fontWeight: 600,
      }}
    >
      {keepFinalWordsTogether(heading)}
    </h2>
    {onAddRecipe && (
      <Button
        onClick={onAddRecipe}
        data-testid="first-recipe-add-btn"
        className={`mt-3 focus:outline-none focus:ring-0 ${fullWidthButton ? 'w-full' : ''}`}
        style={{ backgroundColor: '#51120A', borderColor: '#51120A', color: 'white', fontFamily: "'Bricolage Grotesque', sans-serif" }}
      >
        Add Recipe
      </Button>
    )}
  </div>
);

const fade = 'linear-gradient(to bottom, rgba(238,231,216,0) 45%, rgba(238,231,216,1) 100%)';

const EmptyCollectionState = ({ heading, onAddRecipe }) => (
  <div data-testid="first-recipe-empty-state">
    {/* Desktop */}
    <div className="hidden lg:block" style={{ margin: '0 -8px', padding: '0 8px', overflow: 'hidden' }}>
      <div className="relative" style={{ width: '102.06%', marginLeft: '-0.92%' }}>
        <img src="/desktop-login.svg" alt="" className="w-full h-auto select-none" draggable="false" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: fade }} />
        <div className="absolute" style={{ top: '2.43%', left: '0.9%', width: '31.2%', height: '27.2%' }}>
          <HeadingCard
            heading={heading}
            onAddRecipe={onAddRecipe}
            headingSize="clamp(28px, 3.4vw, 48px)"
          />
        </div>
      </div>
    </div>
    {/* Tablet */}
    <div className="hidden md:block lg:hidden" style={{ margin: '0 -8px', padding: '0 8px', overflow: 'hidden' }}>
      <div className="relative" style={{ width: '104.07%', marginLeft: '-2.03%' }}>
        <img src="/ipad-login.svg" alt="" className="w-full h-auto select-none" draggable="false" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: fade }} />
        <div className="absolute" style={{ top: '1.18%', left: '1.95%', width: '63%', height: '23.9%' }}>
          <HeadingCard
            heading={heading}
            onAddRecipe={onAddRecipe}
            headingSize="clamp(28px, 5.5vw, 52px)"
          />
        </div>
      </div>
    </div>
    {/* Phone */}
    <div className="md:hidden block" style={{ margin: '0 -8px', padding: '0 8px', overflow: 'hidden' }}>
      <div className="relative" style={{ width: '115.5%', marginLeft: '-7.2%' }}>
        <img src="/iphone-login.svg" alt="" className="w-full h-auto select-none" draggable="false" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: fade }} />
        <div className="absolute" style={{ top: '2.23%', left: '6.25%', width: '86.6%', height: '13.3%' }}>
          <HeadingCard
            heading={heading}
            onAddRecipe={onAddRecipe}
            headingSize="clamp(26px, 8.5vw, 36px)"
            fullWidthButton
          />
        </div>
      </div>
    </div>
  </div>
);

export default EmptyCollectionState;
